import {
  Activity,
  CircleHelp,
  Info,
  Sparkles,
  Keyboard,
  KeyRound,
  LayoutGrid,
  Network as NetworkIcon,
  Rocket,
  type LucideIcon,
} from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useAuth, usePermission } from '../auth/context';
import { strings } from '../strings';
import { MenuButton } from './Menu';
import { railSection } from './routeContext';
import { useShell } from './shellContext';

const s = strings.shell.rail;

function RailLink({
  to,
  label,
  icon: Icon,
  current,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  current: boolean;
}) {
  return (
    <li>
      {/* From 768 to 1023 px only the icon shows; the label stays the accessible name and is the tooltip. */}
      <Link to={to} className="rail-item" aria-current={current ? 'page' : undefined} title={label}>
        <Icon aria-hidden className="size-5" />
        <span className="rail-label">{label}</span>
      </Link>
    </li>
  );
}

/** The navigation rail (spec 6.1.8): destinations, and Help at the bottom. */
export function Rail() {
  const { config } = useAuth();
  const { openShortcuts } = useShell();
  const canOperate = usePermission('operate');
  const networking = config.networking?.enabled ?? false;
  const { pathname } = useLocation();
  const section = railSection(pathname);
  return (
    <nav data-region="rail" tabIndex={-1} aria-label={s.label} className="rail">
      <ul className="flex flex-col gap-0.5">
        <RailLink
          to="/blueprints"
          label={s.blueprints}
          icon={LayoutGrid}
          current={section === 'blueprints'}
        />
        <RailLink to="/deployments" label={s.deployments} icon={Rocket} current={section === 'deployments'} />
        {networking && (
          <RailLink
            to="/networks"
            label={strings.networks.nav.networks}
            icon={NetworkIcon}
            current={section === 'networks'}
          />
        )}
        <RailLink to="/runs" label={s.runs} icon={Activity} current={section === 'runs'} />
        {canOperate && (
          <RailLink
            to="/cloud-accounts"
            label={s.cloudAccounts}
            icon={KeyRound}
            current={section === 'cloud-accounts'}
          />
        )}
      </ul>
      <MenuButton
        wrapperClassName="mt-auto flex"
        buttonClassName="rail-item"
        buttonCurrent={section === 'help'}
        buttonTitle={s.help}
        buttonContent={
          <>
            <CircleHelp aria-hidden className="size-5" />
            <span className="rail-label">{s.help}</span>
          </>
        }
        menuLabel={s.help}
        placement="right-end"
        entries={[
          {
            kind: 'link',
            id: 'about',
            label: s.about,
            to: '/about',
            current: pathname === '/about',
            icon: <Info aria-hidden className="size-4" />,
          },
          {
            kind: 'link',
            id: 'whats-new',
            label: s.whatsNew,
            to: '/whats-new',
            current: pathname === '/whats-new',
            icon: <Sparkles aria-hidden className="size-4" />,
          },
          {
            kind: 'action',
            id: 'shortcuts',
            label: s.shortcuts,
            icon: <Keyboard aria-hidden className="size-4" />,
            hint: <kbd className="kbd">?</kbd>,
            onSelect: openShortcuts,
          },
        ]}
        footer={<p className="menu-note border-t border-line">{s.version(config.version)}</p>}
      />
    </nav>
  );
}
